import React, { useState, useEffect } from 'react';
import { fetchWeekendArchive } from '../api/client';
import RankingTable from '../components/RankingTable';
import Poster from '../components/Poster';
import Podium, { MEDALS } from '../components/Podium';
import { MoreButton } from '../components/VenueCharts';
import { fmtShortRange } from '../components/DateRangePicker';
import { PAGE_MAX, fmt, card, ltr, Head, Stepper, SubTitle, Change, Occ } from '../components/RankingParts';
import useWindowWidth from '../hooks/useWindowWidth';

/**
 * סופי שבוע (§12.13, formerly "ארכיון") — one year of Thu–Sun weekends:
 * the year in the card's title with ‹ ›, its total on the left, a podium of
 * its five biggest weekends, then every weekend newest first with its place
 * in the year (1-3 in medal colours), change against the weekend before,
 * occupancy, new films and the leading film. A weekend opens in טבלאות —
 * the films of a weekend are listed in one place only.
 */

const filmName = (m) => m.title_he || m.title;
const filmPoster = (m, size) => <Poster posterUrl={m.poster_url} genre={m.genre} letter={(filmName(m) || '?')[0]} size={size} />;
const dates = (w) => ltr(fmtShortRange(w.start, w.end));
const placeColor = (p) => (p < 3 ? MEDALS[p].c : 'var(--muted)');

function ArchivePage({ onMovieClick, onNavigate, refreshNonce, year: yearProp, onYearChange }) {
  const [own, setOwn] = useState(null);       // null = the server's default, the current year
  const year = yearProp !== undefined ? yearProp : own;
  const setYear = onYearChange || setOwn;
  const mob = useWindowWidth() < 768;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shown, setShown] = useState(20);

  useEffect(() => {
    setLoading(true);
    fetchWeekendArchive(year)
      .then((d) => { setData(d); setLoading(false); })
      .catch(() => { setData(null); setLoading(false); });
  }, [year, refreshNonce]);
  useEffect(() => { setShown(20); }, [year]);

  const weekends = data?.weekends || [];
  const years = data?.years || [];            // newest first
  const at = years.indexOf(data?.year);
  const byTickets = [...weekends].sort((a, b) => b.total_tickets - a.total_tickets);
  const place = new Map(byTickets.map((w, i) => [w.number, i]));
  const total = weekends.reduce((s, w) => s + (w.total_tickets || 0), 0);
  const screenings = weekends.reduce((s, w) => s + (w.total_screenings || 0), 0);
  const open = (w) => onNavigate && onNavigate('charts', { mode: 'weekend', start: w.start, end: w.end });

  const cols = [
    { key: 'number', header: 'סופ״ש', align: 'right', sortValue: (w) => w.number, render: (w) => (
      <div>
        <div style={{ fontWeight: 800, fontSize: 14, color: 'var(--txt)' }}>{dates(w)}</div>
        <div style={{ fontSize: 11, color: 'var(--faint)' }}>סופ״ש {w.number}{w.holiday ? ` · ${w.holiday}` : ''}</div>
      </div>
    ) },
    { key: 'place', header: 'מקום בשנה', align: 'center', sortValue: (w) => -place.get(w.number), render: (w) => {
      const p = place.get(w.number);
      return <span data-place style={{ fontWeight: 800, color: placeColor(p) }}>{p + 1}</span>;
    } },
    { key: 'total_tickets', header: 'כרטיסים', align: 'center', render: (w) => <b style={{ color: 'var(--accent)' }}>{fmt(w.total_tickets)}</b> },
    { key: 'change', header: 'שינוי', align: 'center',
      sortValue: (w) => (w.prev_tickets ? (w.total_tickets - w.prev_tickets) / w.prev_tickets : -Infinity),
      render: (w) => (w.prev_tickets ? <Change curr={w.total_tickets} prev={w.prev_tickets} /> : <span style={{ color: 'var(--faint)' }}>—</span>) },
    { key: 'total_screenings', header: 'הקרנות', align: 'center', render: (w) => <span style={{ color: 'var(--muted)' }}>{fmt(w.total_screenings)}</span> },
    { key: 'occupancy', header: 'תפוסה', align: 'center', render: (w) => <Occ v={w.occupancy} /> },
    { key: 'new_movies', header: 'סרטים חדשים', align: 'center', render: (w) => <span style={{ color: 'var(--muted)' }}>{w.new_movies || '—'}</span> },
    { key: 'top', header: 'מוביל', align: 'right', sortValue: (w) => (w.top_movie ? filmName(w.top_movie) : ''), render: (w) => w.top_movie && (
      <div onClick={(e) => { e.stopPropagation(); onMovieClick && onMovieClick(w.top_movie.movie_id); }}
           style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
        {filmPoster(w.top_movie, 'sm')}
        <div>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--txt)' }}>{filmName(w.top_movie)}</div>
          <div style={{ fontSize: 11, color: 'var(--faint)' }}>{fmt(w.top_movie.tickets)}</div>
        </div>
      </div>
    ) },
  ];

  return (
    <div style={{ maxWidth: PAGE_MAX, margin: '0 auto', width: '100%', display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div data-ranking style={{ ...card, padding: mob ? 16 : 22, opacity: loading ? 0.55 : 1, transition: 'opacity .15s' }}>
        <Head mob={mob} total={total}
          nav={data?.year && <Stepper big label={String(data.year)}
                                       onPrev={() => setYear(years[at + 1])} prevDisabled={at < 0 || at >= years.length - 1}
                                       onNext={() => setYear(years[at - 1])} nextDisabled={at <= 0} />}
          line={weekends.length > 0 && <span>{weekends.length} סופי שבוע · {fmt(screenings)} הקרנות</span>} />
        {weekends.length === 0 ? (
          <div style={{ textAlign: 'center', color: 'var(--faint)', padding: 30, fontSize: 13 }}>
            {loading ? 'טוען נתונים...' : 'אין נתונים לשנה זו'}
          </div>
        ) : (
          <>
            <SubTitle first>הגדולים של {data.year}</SubTitle>
            <Podium items={byTickets} mob={mob} max={5} itemKey={(w) => w.number} onClick={open}
              poster={(w) => (w.top_movie ? filmPoster(w.top_movie, 'xl') : <Poster letter="?" size="xl" />)}
              name={dates} value={(w) => w.total_tickets}
              extra={(w) => (w.holiday || w.top_movie) && (
                <span style={{ fontSize: 11, color: 'var(--faint)' }}>{w.holiday || filmName(w.top_movie)}</span>
              )} />
            <SubTitle>כל סופי השבוע</SubTitle>
            {mob ? (
              <div data-phone-list>
                {weekends.slice(0, shown).map((w) => {
                  const p = place.get(w.number);
                  return (
                    <div key={w.number} data-phone-row onClick={() => open(w)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--brd)', cursor: 'pointer' }}>
                      <div style={{ position: 'relative', flexShrink: 0 }}>
                        {w.top_movie ? filmPoster(w.top_movie, 'sm') : <Poster letter="?" size="sm" />}
                        {p < 3 && (
                          <div data-medal style={{ position: 'absolute', top: -5, right: -5, width: 18, height: 18, borderRadius: 9, background: MEDALS[p].c,
                                                   color: '#fff', fontSize: 10, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{p + 1}</div>
                        )}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div data-row-name style={{ fontSize: 13.5, fontWeight: 800, color: 'var(--txt)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {dates(w)}{w.holiday && <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--faint)' }}> · {w.holiday}</span>}
                        </div>
                        {w.top_movie && <div style={{ fontSize: 10.5, color: 'var(--faint)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>מוביל: {filmName(w.top_movie)}</div>}
                      </div>
                      <div style={{ textAlign: 'center', flexShrink: 0, whiteSpace: 'nowrap' }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)' }}>{fmt(w.total_tickets)}</div>
                        <div style={{ fontSize: 10, color: 'var(--faint)' }}>
                          {w.prev_tickets ? <Change curr={w.total_tickets} prev={w.prev_tickets} size={10} /> : '—'} · <Occ v={w.occupancy} />
                        </div>
                        <div style={{ fontSize: 10, color: 'var(--faint)' }}>{fmt(w.total_screenings)} הקרנות</div>
                      </div>
                    </div>
                  );
                })}
                {weekends.length > shown && <MoreButton onClick={() => setShown((n) => n + 20)}>הצג עוד</MoreButton>}
              </div>
            ) : (
              <RankingTable columns={cols} data={weekends} pageSize={20} minWidth={760} onRowClick={open} />
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default ArchivePage;
